import { Button, Card, Col, Flex, Grid, Spacing } from "@p0security/ui";
import { compact } from "lodash";
import React, { useCallback } from "react";
import { spacing } from "styles/spacing";

import { Splash } from "../../Splash";
import { Footer } from "./Footer";

export type PromptCardProps = {
  title: string;
  features: React.ReactNode[];
  disabled?: boolean;
  disabledText?: JSX.Element;
  onSecondary?: {
    text: string;
    shortText?: string;
    onClick: () => void;
  };
  onPrimary: {
    text: string;
    shortText?: string;
    onClick: () => void;
  };
};

const PromptCard = React.memo(
  ({
    title,
    features,
    disabled,
    disabledText,
    onSecondary,
    onPrimary,
  }: PromptCardProps) => {
    const { xxl, xl } = Grid.useBreakpoint();
    const handlePrimary = useCallback(() => {
      onPrimary.onClick();
    }, [onPrimary]);

    const handleSecondary = useCallback(() => {
      onSecondary?.onClick();
    }, [onSecondary]);

    return (
      <Spacing asChild m="auto">
        <Card title={title} className="w-1/2">
          <Flex
            vertical
            gap={spacing.scale.sm}
            /* eslint-disable-line react/forbid-component-props */ style={{
              height: xxl ? "15em" : "20em",
              overflow: "hidden",
            }}
          >
            {features.map((feature) => (
              <div key={`${title}-${feature}`}>{feature}</div>
            ))}
          </Flex>

          <Spacing mt={spacing.scale.sm}>
            <Flex align="baseline" gap="0.5em" justify="space-between" wrap>
              {onSecondary && (
                <Button
                  /* eslint-disable-line react/forbid-component-props */ style={{
                    width: "45%",
                  }}
                  onClick={handleSecondary}
                >
                  {!xl && onSecondary.shortText
                    ? onSecondary.shortText
                    : onSecondary.text}
                </Button>
              )}
              <Button
                disabled={disabled}
                type="primary"
                /* eslint-disable-line react/forbid-component-props */ style={{
                  width: "45%",
                }}
                onClick={handlePrimary}
              >
                {disabled
                  ? xl
                    ? disabledText
                    : "Soon"
                  : !xl && onPrimary.shortText
                    ? onPrimary.shortText
                    : onPrimary.text}
              </Button>
            </Flex>
          </Spacing>
        </Card>
      </Spacing>
    );
  }
);

PromptCard.displayName = "PromptCard";

const InstallPromptStep = React.memo(
  ({
    prompts,
  }: {
    // The UI can only display up to 3 prompts at a time.
    prompts: [PromptCardProps, PromptCardProps?, PromptCardProps?];
  }) => {
    return (
      <Splash overrideColumn>
        <Col xs={22} md={16} lg={14} xl={12}>
          <Card variant="borderless">
            <Spacing mb={spacing.scale.sm}>
              <Flex gap={spacing.layout.grid.gap.xs}>
                {compact(prompts).map((prompt, ix) => (
                  <PromptCard key={`${ix}-${prompt.title}`} {...prompt} />
                ))}
              </Flex>
            </Spacing>

            <Footer />
          </Card>
        </Col>
      </Splash>
    );
  }
);

InstallPromptStep.displayName = "InstallPromptStep";

export { InstallPromptStep, PromptCard };
